// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Reading an estimate out of whatever file the estimator already has.
 *
 * This existed once, inline in the project picker, where it could only ever
 * produce a brand new project. The same pipeline is what lets somebody load
 * last year's versions into a project's history, which is the thing that makes
 * the history worth having on day one — so it lives here now, and both callers
 * use it rather than keeping two copies that drift.
 */

export type ImportFileType = "pdf" | "csv" | "excel" | "text";

export interface ImportWarnings {
  invalidNumbers?: number;
  suspectValues?: number;
  duplicatesRemoved?: number;
}

/** What the server needs to be told the content is. */
export function fileTypeFor(filename: string): ImportFileType {
  const ext = String(filename || "").split(".").pop()?.toLowerCase() || "";
  if (ext === "pdf") return "pdf";
  if (ext === "csv") return "csv";
  if (ext === "xlsx" || ext === "xls" || ext === "xlsm") return "excel";
  return "text";
}

/**
 * Pulls whole events out of a server-sent-event buffer.
 *
 * Returns the leftover separately because a chunk boundary lands mid-line far
 * more often than it looks like it should, and a half-parsed line thrown away
 * is a silently truncated estimate.
 */
export function parseSSE(buffered: string): { events: any[]; rest: string } {
  const lines = buffered.split("\n");
  const rest = lines.pop() ?? "";
  const events: any[] = [];
  for (const line of lines) {
    if (!line.startsWith("data: ")) continue;
    const payload = line.slice(6);
    if (!payload || payload === "[DONE]") continue;
    try {
      events.push(JSON.parse(payload));
    } catch {
      // A line that is not JSON is not an event. Dropping it is right; the
      // stream carries keep-alives and the server's own progress chatter.
    }
  }
  return { events, rest };
}

/** Human sentence for whatever the server had to clean up. Null when clean. */
export function warningSummary(w: ImportWarnings | null | undefined): string | null {
  if (!w) return null;
  const parts: string[] = [];
  if (w.invalidNumbers) parts.push(`${w.invalidNumbers} invalid number${w.invalidNumbers === 1 ? "" : "s"} reset to 0`);
  if (w.suspectValues) parts.push(`${w.suspectValues} unusually large value${w.suspectValues === 1 ? "" : "s"}`);
  if (w.duplicatesRemoved) parts.push(`${w.duplicatesRemoved} duplicate line${w.duplicatesRemoved === 1 ? "" : "s"} removed`);
  return parts.length ? parts.join(", ") : null;
}

/** Sum of every included line, which is the number an estimator will check. */
export function estimateSubtotal(state: any): number {
  let total = 0;
  const walk = (rows: any[]) => {
    for (const r of rows || []) {
      if (r.type === "group" || r.children) walk(r.children || []);
      if (r.rows) walk(r.rows);
      const li = state?.itemState?.[r.id];
      if (!li || li.included === false) continue;
      total += (Number(li.qty) || 0) * (Number(li.unitCost) || 0);
    }
  };
  for (const div of state?.divisions || []) {
    for (const sub of div.subcategories || []) walk(sub.rows || []);
  }
  return total;
}

/** Reads a file into the text the importer sends. Browser only. */
export async function readEstimateFile(file: File): Promise<{ content: string; fileType: ImportFileType }> {
  const fileType = fileTypeFor(file.name);

  if (fileType === "pdf") {
    const pdfjs = await import("pdfjs-dist");
    const workerMod = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
    (pdfjs as any).GlobalWorkerOptions.workerSrc = (workerMod as any).default;
    const buf = await file.arrayBuffer();
    const pdf = await (pdfjs as any).getDocument({ data: buf }).promise;
    let text = "";
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const tc = await page.getTextContent();
      text += tc.items.map((item: any) => item.str).join(" ") + "\n";
    }
    // The server has no PDF reader; it gets the text we pulled out. A scanned
    // or drawing-only PDF yields nothing, and sending an empty document is a
    // slower way of getting a worse error.
    if (text.replace(/\s+/g, "").length < 40) {
      throw new Error(
        "No text could be read from that PDF — it is probably a scan or an image export. "
        + "An Excel or CSV version of the estimate will import; so will a PDF printed from the spreadsheet.",
      );
    }
    return { content: text, fileType: "text" };
  }

  const XLSX = await import("xlsx");
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  return { content: XLSX.utils.sheet_to_csv(sheet), fileType };
}

/** Parses an estimate file into project state. Throws with a readable message. */
export async function importEstimateFile(
  file: File,
  accessToken: string,
  onProgress?: (charsSoFar: number) => void,
  /** The firm's own cost codes, so the import lands in their taxonomy and not
   *  the built-in one. Omitted means the server keeps its defaults. */
  costCodeCatalog?: string,
  /** Fired the instant a request actually goes out. A caller pacing itself
   *  against the server's rate limit must count requests, not attempts: a file
   *  rejected here never reached the server and must not cost a slot. */
  onRequest?: () => void,
): Promise<{ projectState: any; warnings: ImportWarnings | null }> {
  const { content, fileType } = await readEstimateFile(file);

  // The edge function caps the request body. Failing here names the problem;
  // failing at the platform returns an HTML error page the app cannot read.
  const MAX_CHARS = 3_000_000;
  if (content.length > MAX_CHARS) {
    throw new Error(
      `That file reads as ${(content.length / 1_000_000).toFixed(1)} million characters, past what the importer accepts. `
      + "Exporting just the estimate sheet, or saving it as CSV, usually brings it well under.",
    );
  }
  if (content.replace(/\s+/g, "").length < 40) {
    throw new Error("That file appears to be empty — nothing readable came out of it.");
  }

  onRequest?.();
  const resp = await fetch("/api/ai-import", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ content, fileType, fileName: file.name, costCodeCatalog }),
  });

  if (!resp.ok) {
    // "Import failed" on its own tells nobody anything, including me. When the
    // body is not JSON the failure happened above the app -- a payload limit, a
    // timeout, a gateway page -- so the status is the only clue there is, and
    // it has to reach the person looking at the screen.
    const raw = await resp.text().catch(() => "");
    let message = "";
    try { message = JSON.parse(raw)?.error || ""; } catch { /* not JSON */ }

    if (!message) {
      // The Vite dev server serves the app but not /api — those are Vercel
      // functions that exist only in a deployment. A 404 on localhost is that,
      // every time, and reads as a broken endpoint unless it says so.
      // Guarded: this module is unit-tested under Node, where there is no
      // location, and an unguarded read throws before any message is built.
      const host = typeof location !== "undefined" ? location.hostname : "";
      const onDevServer = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(host);
      if (resp.status === 404 && onDevServer) {
        message = "The dev server does not run the /api functions — they only exist in a deployment. "
          + "Try this on the deployed site.";
      } else if (resp.status === 404) {
        message = "The importer endpoint was not found. The deployment may still be building; "
          + "give it a minute and try again.";
      } else if (resp.status === 413) {
        message = "That file is too large to send for reading. Try exporting just the estimate sheet, or save it as CSV.";
      } else if (resp.status === 504 || resp.status === 408) {
        message = "Reading that file took too long and timed out. A smaller file, or just the estimate sheet, usually works.";
      } else {
        const snippet = raw.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 140);
        message = `Import failed (HTTP ${resp.status})${snippet ? `: ${snippet}` : ""}`;
      }
    }
    throw new Error(message);
  }

  const reader = resp.body!.getReader();
  const decoder = new TextDecoder();
  let buffered = "";
  let projectState: any = null;
  let warnings: ImportWarnings | null = null;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffered += decoder.decode(value, { stream: true });
    const { events, rest } = parseSSE(buffered);
    buffered = rest;
    for (const evt of events) {
      if (evt.type === "progress" && onProgress) onProgress(evt.length || 0);
      if (evt.type === "error") throw new Error(evt.error || "Import failed");
      if (evt.type === "complete") {
        projectState = evt.projectState;
        warnings = evt.warnings || null;
      }
    }
  }

  if (!projectState) throw new Error("The importer returned nothing. Try a different file format.");
  return { projectState, warnings };
}
